// Life & Wealth Solutions - Pending Approvals Component
// Queue of items awaiting Agent 12 signature
//
// FIXED two real bugs found during a full mock-data/dummy-code audit:
// 1. The "production mode" branch called POST /compliance/approve/{id} and
//    /compliance/reject/{id} — neither endpoint has ever existed anywhere
//    in this backend. Every real (non-demo) approve/reject click would
//    404. There was no way for Agent 12 to actually approve or reject
//    anything in production before this fix.
// 2. The GET call read response.data.approvals, but the real backend
//    endpoint (GET /agent12/pending-approvals/{agency_id}) has always
//    returned its list under response.data.items, not .approvals — so
//    even loading the queue silently returned an empty list in production
//    regardless of how many real items were actually pending.
//
// Real endpoints now used: GET /agent12/pending-approvals/{agencyId},
// POST /agent12/approvals/{type}/{id}/approve, POST /agent12/approvals/{type}/{id}/reject
// (all three are real, newly added/fixed, and backed by actual DB writes.)

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { isDemoMode, mockData } from '../../services/mockDataService';
import { useAuth } from '../../hooks/useAuth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

type ItemType = 'policy' | 'intervention' | 'escalation';

interface PendingApproval {
  approval_id: string;
  item_type: ItemType;
  type: 'compliance_override' | 'regulatory_adjustment' | 'manual_review' | 'whale_binding';
  policy_id?: string;
  client_name?: string;
  description: string;
  agent_11_notes?: string;
  priority: 'high' | 'medium' | 'low' | 'critical';
  created_at: string;
}

interface Props {
  userId: string; // Agent 12's own user_id
}

const TYPE_LABELS: Record<PendingApproval['type'], string> = {
  compliance_override: 'Compliance Override',
  regulatory_adjustment: 'Regulatory Adjustment',
  manual_review: 'Manual Review',
  whale_binding: 'Whale Policy Binding',
};

function mapServerItemToApproval(item: any): PendingApproval {
  const itemType: ItemType = item.type;
  const data = item.data || {};

  if (itemType === 'policy') {
    return {
      approval_id: item.id,
      item_type: 'policy',
      type: 'whale_binding',
      policy_id: data.policy_id,
      client_name: data.client_name,
      description: `${data.policy_type || 'Policy'} — $${Number(data.premium_amount || 0).toLocaleString()} premium, ${data.state || ''}`,
      priority: item.priority,
      created_at: item.timestamp,
    };
  }

  if (itemType === 'intervention') {
    return {
      approval_id: item.id,
      item_type: 'intervention',
      type: 'compliance_override',
      policy_id: data.policy_id,
      client_name: data.client_name,
      description: data.violation_description || data.violation_type || 'Compliance intervention requires review',
      agent_11_notes: data.violation_description,
      priority: item.priority,
      created_at: item.timestamp,
    };
  }

  // escalation
  return {
    approval_id: item.id,
    item_type: 'escalation',
    type: 'regulatory_adjustment',
    policy_id: data.policy_id,
    client_name: data.client_name,
    description: `${data.trigger_type || 'Escalation'} — $${Number(data.premium_amount || 0).toLocaleString()}`,
    priority: item.priority,
    created_at: item.timestamp,
  };
}

const PendingApprovals: React.FC<Props> = ({ userId }) => {
  const { user } = useAuth();
  const [approvals, setApprovals] = useState<PendingApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState<string | null>(null);

  const agencyId = user?.agency_id;

  const loadApprovals = useCallback(async () => {
    try {
      if (isDemoMode()) {
        const mockApprovals = mockData.approvals();
        setApprovals(mockApprovals);
        setError('');
        setLoading(false);
        return;
      }

      if (!agencyId) {
        setError('No agency associated with this account');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/agent12/pending-approvals/${agencyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const items = response.data.items || [];
      setApprovals(items.map(mapServerItemToApproval));
      setError('');
    } catch (err: any) {
      console.error('Failed to load approvals:', err);
      setError(err.response?.data?.detail || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, [agencyId]);

  useEffect(() => {
    loadApprovals();
  }, [loadApprovals]);

  const handleApprove = async (approval: PendingApproval) => {
    if (!window.confirm('Approve this action? Your signature will be recorded.')) return;
    setProcessing(approval.approval_id);

    try {
      if (isDemoMode()) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        alert('✅ Approved! Action logged with your signature. (Demo Mode)');
        setApprovals(approvals.filter(a => a.approval_id !== approval.approval_id));
        return;
      }

      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/agent12/approvals/${approval.item_type}/${approval.approval_id}/approve`,
        { agent_12_user_id: userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('✅ Approved! Action logged with your signature.');
      await loadApprovals();
    } catch (err: any) {
      alert(`❌ Approval failed: ${err.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (approval: PendingApproval) => {
    const reason = prompt('Rejection reason (required):');
    if (!reason) return;
    setProcessing(approval.approval_id);

    try {
      if (isDemoMode()) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        alert('❌ Rejected. My Sentinel has been notified. (Demo Mode)');
        setApprovals(approvals.filter(a => a.approval_id !== approval.approval_id));
        return;
      }

      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_URL}/agent12/approvals/${approval.item_type}/${approval.approval_id}/reject`,
        { agent_12_user_id: userId, notes: reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('❌ Rejected. My Sentinel has been notified.');
      await loadApprovals();
    } catch (err: any) {
      alert(`❌ Rejection failed: ${err.response?.data?.detail || 'Unknown error'}`);
    } finally {
      setProcessing(null);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'border-red-700 bg-red-100';
      case 'high': return 'border-red-500 bg-red-50';
      case 'medium': return 'border-yellow-500 bg-yellow-50';
      case 'low': return 'border-green-500 bg-green-50';
      default: return 'border-gray-500 bg-gray-50';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'critical': return '🔴';
      case 'high': return '🔴';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Loading pending approvals...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  if (approvals.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">✅</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">All Clear!</h3>
        <p className="text-gray-600">No approvals pending. Your AI agents are operating autonomously.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          ⏳ {approvals.length} Item{approvals.length === 1 ? '' : 's'} Awaiting Your Signature
        </h3>
        <button onClick={loadApprovals} className="text-sm text-blue-600 hover:text-blue-800">🔄 Refresh</button>
      </div>

      {approvals.map((approval) => (
        <div key={approval.approval_id} className={`border-l-4 rounded-lg p-6 ${getPriorityColor(approval.priority)}`}>
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">{getPriorityIcon(approval.priority)}</span>
                <span className="inline-block bg-blue-100 text-blue-800 text-xs font-semibold px-3 py-1 rounded-full">
                  {TYPE_LABELS[approval.type]}
                </span>
                {(approval.priority === 'high' || approval.priority === 'critical') && (
                  <span className="inline-block bg-red-100 text-red-800 text-xs font-semibold px-3 py-1 rounded-full">
                    {approval.priority.toUpperCase()} PRIORITY
                  </span>
                )}
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-1">{approval.client_name || 'System Action'}</h4>
              <p className="text-sm text-gray-700">{approval.description}</p>
              {approval.policy_id && (
                <p className="text-xs text-gray-500 mt-1">Policy ID: {approval.policy_id}</p>
              )}
            </div>
          </div>

          {approval.agent_11_notes && (
            <div className="bg-white rounded-lg p-4 mb-4 border border-gray-200">
              <div className="flex items-start gap-2">
                <span className="text-lg">🛡️</span>
                <div className="flex-1">
                  <div className="font-medium text-gray-900 mb-1">My Sentinel (The General):</div>
                  <p className="text-sm text-gray-700">{approval.agent_11_notes}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={() => handleApprove(approval)}
              disabled={processing === approval.approval_id}
              className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-lg transition-colors"
            >
              {processing === approval.approval_id ? 'Processing...' : '✅ Approve & Sign'}
            </button>
            <button
              onClick={() => handleReject(approval)}
              disabled={processing === approval.approval_id}
              className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-lg transition-colors"
            >
              {processing === approval.approval_id ? 'Processing...' : '❌ Reject'}
            </button>
          </div>

          <div className="mt-3 text-xs text-gray-500 text-right">
            Created: {new Date(approval.created_at).toLocaleString()}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PendingApprovals;
